// Own link to a signed inspection's PDF (roadmap 2026-Q4, M3): a random
// 128-bit token served at app.tallerza.cl/d/<token> instead of Storage's
// getDownloadURL, which never expires and exposes the customer's RUT, phone
// and signature to anyone the link is forwarded to. The server (Function
// enlacePdf) checks validity, revocation and workshop, then redirects to a
// short-lived signed Storage URL. Behind the `enlacesPdf` switch of the
// workshop's general configuration (off by default).
import { z } from 'zod';

/** Validity of a link (owner's decision D4): 90 days, revocable before. */
export const VIGENCIA_ENLACE_PDF_DIAS = 90;
export const VIGENCIA_ENLACE_PDF_MS = VIGENCIA_ENLACE_PDF_DIAS * 24 * 60 * 60 * 1000;

/** Public base of the links (Hosting rewrite /d/** of the `tallerza` site). */
export const BASE_ENLACE_PDF = 'https://app.tallerza.cl/d/';

/** 16 random bytes in base64url without padding: exactly 22 characters. */
export const TOKEN_ENLACE_PDF = /^[A-Za-z0-9_-]{22}$/;

export const urlEnlacePdf = (token: string) => `${BASE_ENLACE_PDF}${token}`;

/**
 * Token of a request path: exactly `/d/<token>`, the path Hosting forwards
 * through the rewrite. Anything else (including `/<token>` on the function's
 * own URL) is null and answered exactly like an unknown token.
 */
export function tokenDeRuta(ruta: string | undefined): string | null {
  const t = /^\/d\/([^/]+)$/.exec(ruta ?? '')?.[1];
  return t && TOKEN_ENLACE_PDF.test(t) ? t : null;
}

/**
 * The link as the server exposes it on the inspection document (field
 * `enlacePdf`, next to `pdfPath`), so the app and the backoffice share it
 * instead of a download URL. Server-written only (the inspection rules are an
 * allow-list of client fields). `expiraEn`/`revocadoEn` are Firestore
 * Timestamps written by the server.
 */
export type EnlacePdfInspeccion = {
  url: string;
  expiraEn: unknown;
  revocadoEn?: unknown;
};

const idDoc = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);

/**
 * Callables of the workshop's admin or jefe de taller: revocarEnlacePdf
 * revokes the inspection's current link (also while the workshop is
 * suspended: it only protects); emitirEnlacePdf revokes it (if any) and
 * issues a new one for 90 days (needs the switch on and the PDF).
 */
export const esquemaEnlacePdfInspeccion = z.object({ inspeccionId: idDoc });
export type SolicitudEnlacePdf = z.infer<typeof esquemaEnlacePdfInspeccion>;

export const MENSAJES_ENLACE_PDF = {
  /** 404: unknown or malformed token. Same text for both: no hint. */
  noEncontrado: 'Este enlace no existe. Revisa que esté completo o pide uno nuevo al taller.',
  /** 410: expired, revoked, switched off or no longer available. Same text for all. */
  noDisponible: 'Este enlace ya no está disponible. Pide uno nuevo al taller.',
  limite: 'Demasiados intentos. Espera un momento y vuelve a intentar.',
  interno: 'No se pudo abrir el documento. Intenta de nuevo en unos minutos.',
} as const;
